// Parse a CPLEX version node's agent-authored `dynamic_metadata` (title +
// summary + the list of changes the agent made). The agent already submits a
// `changes` list — each {kind, label} a constraint/assumption/parameter/
// objective it added — so a version is far more than its title; this surfaces
// the full list. Pure → unit-tested.

export interface VersionChange {
  kind: string | null;
  label: string;
}

export interface VersionMeta {
  title: string | null;
  summary: string | null;
  changes: VersionChange[];
}

const KIND_COLORS: Record<string, string> = {
  constraint: '#1d4ed8',
  assumption: '#7c3aed',
  parameter: '#b45309',
  objective: '#16a34a',
  base: '#64748b',
};

export function kindColor(kind: string | null): string {
  return (kind && KIND_COLORS[kind.toLowerCase()]) || '#64748b';
}

export function parseVersionMeta(
  dynamicMetadata: Record<string, unknown> | null | undefined,
): VersionMeta {
  const md = dynamicMetadata ?? {};
  const title = typeof md.title === 'string' ? md.title : null;
  const summary = typeof md.summary === 'string' ? md.summary : null;
  const raw = (md as { changes?: unknown }).changes;
  const changes: VersionChange[] = Array.isArray(raw)
    ? raw
        .filter((c): c is Record<string, unknown> => !!c && typeof c === 'object')
        .map((c) => ({
          kind: typeof c.kind === 'string' ? c.kind : null,
          label: typeof c.label === 'string' ? c.label : '',
        }))
        .filter((c) => c.label.trim() !== '')
    : [];
  return { title, summary, changes };
}
